import React from "react"

import {
  prefetchCustomerCompanyInvitationListQuery,
  prefetchCustomerMembershipListQuery,
  useCustomerCompanyInvitationListSuspense,
  useCustomerCompanyList,
  useCustomerMembershipListSuspense,
} from "@boundless/bl-api-client/customer/services"
import { createFileRoute, Link } from "@tanstack/react-router"

import { CompanyFilter } from "@/components/membership/CompanyFilter"
import { CompanyInvitationDialog } from "@/components/membership/CompanyInvitationDialog"
import {
  FallbackMembershipTable,
  MembershipTable,
} from "@/components/membership/MembershipTable"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { companyListQueryOptions } from "@/lib/companies"

export const Route = createFileRoute("/_dashboard/_customer/users/")({
  component: UsersView,
  loader: ({ context: { queryClient, companyUuid } }) => {
    prefetchCustomerMembershipListQuery(queryClient, companyUuid)
    prefetchCustomerCompanyInvitationListQuery(queryClient, companyUuid)
  },
})

function MembershipList({ companyUuid }: { companyUuid: string }) {
  const { data: memberships } = useCustomerMembershipListSuspense(companyUuid)
  const { data: invitations } =
    useCustomerCompanyInvitationListSuspense(companyUuid)

  // Owners first, then alphabetically. Copy before sorting: the query result is
  // the TanStack cache and `sort` mutates in place.
  const rows = [...memberships].sort((a, b) => {
    if (a.role !== b.role) {
      return a.role === "owner" ? -1 : 1
    }
    return a.user.firstName.localeCompare(b.user.firstName)
  })

  return (
    <MembershipTable
      memberships={rows}
      invitations={invitations}
      companyUuid={companyUuid}
    />
  )
}

/**
 * Invite trigger for the company the table is scoped to, which is the active
 * company unless `?company=` says otherwise. Renders nothing until the company
 * list resolves, because the dialog copy names the target company.
 */
function InviteTeammate({ companyUuid }: { companyUuid: string }) {
  const { data: companies } = useCustomerCompanyList(
    {},
    companyListQueryOptions(),
  )

  const company = companies?.find((c) => c.uuid === companyUuid)
  if (!company) {
    return null
  }

  return (
    <CompanyInvitationDialog
      companyUuid={company.uuid}
      companyName={company.legalName}
    />
  )
}

function UsersView() {
  const { companyUuid } = Route.useRouteContext()
  const navigate = Route.useNavigate()

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Users</CardTitle>
        <div className="flex items-center gap-2">
          <Button asChild variant="link">
            <Link to="/users/activity" search={{ company: companyUuid }}>
              Activity
            </Link>
          </Button>
          <InviteTeammate companyUuid={companyUuid} />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <CompanyFilter
          companyUuid={companyUuid}
          onSelect={(uuid) => navigate({ search: { company: uuid } })}
        />
        {/* Keyed so switching company shows the skeleton again instead of the
            previous company's rows while the next list loads. */}
        <React.Suspense
          key={companyUuid}
          fallback={<FallbackMembershipTable />}
        >
          <MembershipList companyUuid={companyUuid} />
        </React.Suspense>
      </CardContent>
    </Card>
  )
}
